import { useEffect, useMemo, useRef } from 'react'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { EventContentArg } from '@fullcalendar/core'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import type { ScheduleEncounter } from '../service'

const DATE_FORMAT = 'YYYY-MM-DD'

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending Confirmation',
  booked: 'Confirmed',
  'checked-in': 'Patient Ready',
  noshow: 'No Show',
  fulfilled: 'Completed',
}

function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return ''
  return (
    APPOINTMENT_STATUS_LABELS[status] ??
    status
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  )
}

type DayScheduleViewProps = {
  selectedDate: string
  encounters?: ScheduleEncounter[]
}

export const DayScheduleView = ({ selectedDate, encounters = [] }: DayScheduleViewProps) => {
  const calendarRef = useRef<FullCalendar | null>(null)
  const navigate = useNavigate()
  const isToday = selectedDate === dayjs().format('YYYY-MM-DD')

  useEffect(() => {
    calendarRef.current?.getApi().gotoDate(selectedDate)
  }, [selectedDate])

  const events = useMemo(() => {
    return encounters
      .filter((encounter) => {
        if (!encounter.periodStart) return false
        return dayjs(encounter.periodStart).format(DATE_FORMAT) === selectedDate
      })
      .map((encounter) => {
        const start = encounter.periodStart
        const end = encounter.periodEnd ?? dayjs(start).add(30, 'minute').toISOString()
        return {
          id: encounter.id,
          title: encounter.patientDisplayName ?? 'Unknown patient',
          start,
          end,
          extendedProps: {
            subtitle: formatStatusLabel(encounter.appointmentStatus ?? encounter.status),
            isPatientReady: (encounter.appointmentStatus ?? encounter.status) === 'checked-in',
          },
        }
      })
  }, [encounters, selectedDate])

  return (
    <div className="flex h-full w-full flex-col">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-[5px]">
        <span className="text-[12px] font-bold text-gray-900">
          {dayjs(selectedDate).format('dddd, MMMM D, YYYY')}
        </span>
      </div>

      <div className="home-day-schedule flex-1 overflow-auto">
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridDay"
          initialDate={selectedDate}
          headerToolbar={false}
          allDaySlot={false}
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          slotDuration="01:00:00"
          slotLabelFormat={{ hour: 'numeric', meridiem: 'short' }}
          dayHeaders={false}
          nowIndicator={isToday}
          height="auto"
          events={events}
          eventBackgroundColor="transparent"
          eventBorderColor="transparent"
          eventContent={(arg: EventContentArg) => {
            const isPatientReady = Boolean(arg.event.extendedProps.isPatientReady)
            const accentColor = isPatientReady ? '#16A34A' : '#e55535'
            return (
              <div
                className="flex h-full w-full cursor-pointer overflow-hidden rounded-sm border-l-[3px] px-2 py-1.5"
                style={{
                  borderColor: accentColor,
                  background: isPatientReady ? '#EFFBF3' : '#fff4f2',
                }}
                onClick={() => navigate(`/call/${arg.event.id}`)}
              >
                <div className="flex flex-col leading-snug">
                  <span className="text-[13px] font-semibold" style={{ color: accentColor }}>
                    {arg.event.title}
                  </span>
                  <span className="text-[12px] text-gray-500">
                    {arg.event.extendedProps.subtitle}
                  </span>
                </div>
              </div>
            )
          }}
        />
      </div>

      <style>{`
        .home-day-schedule .fc {
          --fc-border-color: rgb(229 231 235);
          --fc-now-indicator-color: rgb(239 68 68);
          --fc-today-bg-color: transparent;
          font-family: inherit;
        }
        .home-day-schedule .fc-timegrid-slot {
          height: 5rem;
        }
        .home-day-schedule .fc-timegrid-slot-label {
          border: none;
          color: rgb(107 114 128);
          font-size: 0.75rem;
          vertical-align: top;
          padding-top: 4px;
        }
        .home-day-schedule .fc-timegrid-axis,
        .home-day-schedule .fc-scrollgrid,
        .home-day-schedule .fc-scrollgrid-section > td {
          border: none !important;
        }
        .home-day-schedule .fc-timegrid-col {
          border-left: none !important;
        }
        .home-day-schedule .fc-event {
          border: none !important;
          background: transparent !important;
          padding: 0 !important;
          margin-left: 2px;
          margin-right: 4px;
        }
        .home-day-schedule .fc-timegrid-now-indicator-line {
          border-top-width: 2px;
          border-color: rgb(239 68 68);
        }
        .home-day-schedule .fc-timegrid-now-indicator-arrow {
          display: none;
        }
        .home-day-schedule .fc-timegrid-now-indicator-line::before {
          content: '';
          position: absolute;
          left: -5px;
          top: -5px;
          width: 10px;
          height: 10px;
          border-radius: 9999px;
          background: rgb(239 68 68);
        }
      `}</style>
    </div>
  )
}
